import React, { useState } from 'react'
import classNames from 'classnames'
import { NavigationSidebar } from './NavigationSidebar'
import './NavigationWithSideBar.scss'

export interface ClickNavigationSection {
  id: string
  label: React.ReactNode
  content?: () => React.ReactNode
  disabled?: boolean
  className?: string
  hasError?: boolean
  isLoading?: boolean
}

interface ClickNavigationProps {
  sections: ClickNavigationSection[]
  defaultActiveSectionId: string
  className?: string
  sectionClassName?: string
  onSectionClick?: (id: string) => void
  activeSectionId?: string
  NavigationFooter?: React.ReactNode
}

const ContentArea = React.memo(({ activeSection, sectionClassName }: { activeSection: ClickNavigationSection; sectionClassName?: string }) => {
  return (
    <div className='scroll-navigation__sections'>
      <section
        key={activeSection.id}
        className={classNames('zocks-mb-4 scroll-navigation__section h-100', {
          [`${sectionClassName}`]: sectionClassName,
          [`${activeSection.className}`]: activeSection.className,
        })}
      >
        {activeSection.content ? activeSection.content() : null}
      </section>
    </div>
  )
})

export const ClickNavigation: React.FC<ClickNavigationProps> = ({
  sections,
  defaultActiveSectionId,
  className,
  sectionClassName,
  onSectionClick,
  activeSectionId,
  NavigationFooter,
}) => {
  const [internalActiveSectionId, setInternalActiveSectionId] = useState<string>(defaultActiveSectionId)
  const [resolvedActiveSection, setResolvedActiveSection] = useState<ClickNavigationSection | undefined>(undefined)

  const effectiveActiveSectionId = activeSectionId ?? internalActiveSectionId

  const onNavigationSectionClick = (id: string) => {
    setInternalActiveSectionId(id)
    onSectionClick?.(id)
  }

  const currentSection = sections.find((s) => s.id === effectiveActiveSectionId)

  if (
    resolvedActiveSection?.id !== effectiveActiveSectionId ||
    (resolvedActiveSection &&
      currentSection &&
      (resolvedActiveSection.isLoading !== currentSection?.isLoading || resolvedActiveSection.disabled !== currentSection?.disabled))
  ) {
    setResolvedActiveSection(currentSection)
  }

  return (
    <div className={classNames('scroll-navigation__container', className)}>
      <div className='d-flex flex-column gap-4'>
        <NavigationSidebar
          sections={sections.map(({ id, label, disabled, hasError, isLoading }) => ({ id, label, disabled, hasError, isLoading }))}
          activeSectionId={effectiveActiveSectionId}
          onSectionClick={onNavigationSectionClick}
          NavigationFooter={NavigationFooter}
        />
      </div>
      {resolvedActiveSection && <ContentArea activeSection={resolvedActiveSection} sectionClassName={sectionClassName} />}
    </div>
  )
}
